import React from 'react'
import { graphql } from 'react-apollo'
import { withApollo } from 'react-apollo'

import { ModalActionTypes, Spinner } from '@acorns/web-components'
import camelCase from 'camelcase'
import differenceInYears from 'date-fns/difference_in_years'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import Button from 'components/Button'
import { CenteredSpinner } from 'components/CenteredSpinner/CenteredSpinner'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { QuestionFlowType } from 'pages/LaterSuitabilityQuestions/types'
import { getLocalState } from 'pages/LaterSuitabilityQuestions/utils'
import createIRAWithProfileMutation from 'src/graphql/mutations/createIraWithProfile.mutation.gql'
import { withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import { TestIDs } from '../../test/utils/testIds'
import recommendedPortfolioQuery from './gql/recommendedPortfolio.query.gql'
import registrationComplete from './gql/registrationComplete.mutation.gql'
import Recommendation from './Recommendation'
import {
  Container,
  PricingText,
  RecommendationContainer,
  Subtitle,
  Title,
} from './styled-components'

type Props = {
  loading: boolean
  accounts: Accounts[]
  ageThreshold?: boolean
  submitting?: boolean
  header: string
  text: string
  subscriptionText: string
  buttonText: string
  backendEligibilityFailed: boolean
}

type Accounts = {
  name: string
  details: string[]
  recommendation: string
}

type HandlerProps = {
  handleOpenAccounts: () => void
  setSubmitting: () => void
  setBackendEligibilityFailed: () => void
}

type InnerProps = Props & HandlerProps & RemoteStringsProps

const getAccountDetails = getRemoteString => [
  {
    name: 'later',
    details: [
      getRemoteString('later_feature_1', 'An easy way to save for retirement'),
      getRemoteString('later_feature_2', 'Earn potential tax advantages'),
      getRemoteString('later_feature_3', 'Updated regularly to match your goals'),
    ],
  },
  {
    name: 'invest',
    details: [
      getRemoteString('invest_feature_1', 'Round up spare change'),
      getRemoteString('invest_feature_2', 'Earn bonus investments'),
      getRemoteString('invest_feature_3', 'Personalized portfolio built by experts'),
    ],
  },
]

const LATER_RECOMMENDATIONS = new Map([
  ['TraditionalIRA', 'Traditional'],
  ['SimplifiedEmployeePension', 'Simplified Employee Pension'],
  ['RothIRA', 'Roth'],
])

export const enhance = compose<InnerProps, {}>(
  withApollo,
  withFlowRouter,
  withApolloErrorModal,
  withRemoteStrings,
  withState('submitting', 'setSubmitting', false),
  withState('backendEligibilityFailed', 'setBackendEligibilityFailed', false),
  graphql(recommendedPortfolioQuery, {
    options: { fetchPolicy: 'network-only' },
  }),
  graphql(registrationComplete, {
    name: 'setRegistrationComplete',
  }),
  graphql(createIRAWithProfileMutation, {
    name: 'createIRAWithProfile',
  }),
  withProps(
    ({
      getRemoteString,
      backendEligibilityFailed,
      data: { loading, recommendedPortfolio, user, recommendation, roundUpProfile },
    }) => {
      if (loading) {
        return {
          loading,
        }
      } else {
        const ACCOUNT_DETAILS = getAccountDetails(getRemoteString)
        const age = differenceInYears(new Date(), new Date(user.dob))
        const ageThreshold = age < 69
        const laterDetails = ACCOUNT_DETAILS.find(account => account.name === 'later' && account)
        const coreDetails = ACCOUNT_DETAILS.find(account => account.name === 'invest' && account)

        const laterPortfolio = {
          ...laterDetails,
          recommendation: LATER_RECOMMENDATIONS.get(recommendation),
        }
        const corePortfolio = {
          ...coreDetails,
          recommendation: recommendedPortfolio.name,
        }

        return {
          loading,
          ageThreshold,
          recommendation,
          roundUpsEnabled: pathOr(null, ['roundUpsEnabled'], roundUpProfile),
          accounts:
            ageThreshold && !backendEligibilityFailed
              ? [laterPortfolio, corePortfolio]
              : [corePortfolio],
          header: getRemoteString('portfolio_recommendation_header', 'Recommended for you'),
          text:
            ageThreshold && !backendEligibilityFailed
              ? getRemoteString(
                  'portfolio_recommendation_text',
                  'Based on your goals and information, we recommend opening the following Acorns accounts.',
                )
              : 'Based on your goals and information, we recommend opening the following Acorns account.',
          subscriptionText: getRemoteString(
            'portfolio_recommendation_subscription_text',
            'Your subscription would be',
          ),
          buttonText:
            ageThreshold && !backendEligibilityFailed
              ? getRemoteString('portfolio_recommendation_CTA', 'Open accounts')
              : 'Open account',
        }
      }
    },
  ),
  withHandlers({
    handleCloseErrorModal: ({ closeErrorModal, route, roundUpsEnabled }) => () => {
      closeErrorModal()
      route({
        from: FlowRoute.PortfolioRecommendation,
        action: 'continue',
        state: { roundUpsEnabled },
      })
    },
  }),
  withHandlers({
    handleCloseIneligibleModal: ({ closeErrorModal, setBackendEligibilityFailed }) => () => {
      closeErrorModal()
      setBackendEligibilityFailed(true)
    },
  }),
  withHandlers({
    handleOpenAccounts: ({
      client,
      setRegistrationComplete,
      createIRAWithProfile,
      recommendation,
      setSubmitting,
      route,
      ageThreshold,
      showErrorModal,
      handleCloseErrorModal,
      backendEligibilityFailed,
      handleCloseIneligibleModal,
      roundUpsEnabled,
    }) => async () => {
      setSubmitting(true)

      const profileAttributes = getLocalState(client)
        .storedQuestions.filter(question => question.flowType === QuestionFlowType.Later)
        .map(laterQuestion => {
          return {
            key: camelCase(laterQuestion.questionId),
            value: laterQuestion.answerId,
          }
        })

      const createInvestmentAccount = async () => {
        if (ageThreshold && !backendEligibilityFailed) {
          return await createIRAWithProfile({
            variables: { iraType: recommendation, profileAttributes },
          }).catch(error => {
            const errorCodeMap = new Map([
              [
                'multiple_accounts_not_supported',
                [
                  {
                    title: 'You’ve already done this',
                    message: 'It looks like you already opened up an Acorns Later account.',
                    actions: [
                      {
                        type: ModalActionTypes.primary,
                        label: 'Continue',
                        handler: handleCloseErrorModal,
                      },
                    ],
                  },
                ],
              ],
              [
                'over_age_limit_for_traditional_ira',
                [
                  {
                    title: 'Age limit met',
                    message:
                      'You have to be under 69 years old to open an Acorns Later IRA, but you can keep growing wealth in your existing Acorns account!',
                    actions: [
                      {
                        type: ModalActionTypes.primary,
                        label: 'Okay, got it!',
                        handler: handleCloseIneligibleModal,
                      },
                    ],
                  },
                ],
              ],
            ])

            const errorCode = pathOr(null, [0, 'data', 'errors', 0], error.graphQLErrors)

            if (errorCode && errorCodeMap.get(errorCode)) {
              const { title, message, actions } = errorCodeMap.get(errorCode)[0]

              showErrorModal({
                title,
                message,
                actions,
              })
            }
            setSubmitting(false)
            sendToSentry(error, {
              name: 'PortfolioRecommendation - createInvestmentAccount',
              message: 'Attempting to call createIRAWithProfile - eligible for Later',
            })
            throw new Error(error)
          })
        }
      }

      createInvestmentAccount()
        .then(() => setRegistrationComplete())
        .then(() =>
          ageThreshold && !backendEligibilityFailed
            ? route({
                from: FlowRoute.PortfolioRecommendation,
                action: 'continue',
                state: { roundUpsEnabled },
              })
            : route({
                from: FlowRoute.PortfolioRecommendation,
                action: 'ineligible',
              }),
        )
        .catch(error => {
          setSubmitting(false)
          sendToSentry(error, {
            name: 'PortfolioRecommendation - Reg Complete',
            message: 'Attempting to mark registrationComplete',
          })
        })
    },
  }),
)

export const PortfolioRecommendation = ({
  handleOpenAccounts,
  ageThreshold,
  loading,
  header,
  text,
  buttonText,
  subscriptionText,
  accounts,
  submitting,
  backendEligibilityFailed,
}) =>
  loading ? (
    <CenteredSpinner />
  ) : (
    <>
      <Nav />
      <Layout>
        <Container>
          <Title>{header}</Title>
          <Subtitle>{text}</Subtitle>
          <RecommendationContainer>
            {accounts.map((account, index) => (
              <Recommendation key={index} account={account} />
            ))}
          </RecommendationContainer>
        </Container>
        <Separator />
        <PricingText>
          {subscriptionText}{' '}
          <strong>
            {ageThreshold && !backendEligibilityFailed ? '$2 per month.' : '$1 per month.'}
          </strong>
        </PricingText>
        <Button
          disabled={submitting}
          onPress={handleOpenAccounts}
          data-testid={TestIDs.PORTFOLIO_RECOMMENDATION.OPEN_ACCOUNTS_CTA}
        >
          {submitting ? <Spinner white height={20} width={20} /> : buttonText}
        </Button>
      </Layout>
    </>
  )

export default enhance(PortfolioRecommendation)
